import React, { useCallback, useState } from "react";
import z from "zod";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Select, { SingleValue } from "react-select";
import { Plus, Trash2, Link2, Check, ChevronDown, ChevronRight, ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import Dialog from "@/components/common/Dialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { TaskDependenciesEnumValue } from "@backend/src/schemas/enums";
import { dependenciesTaskSchema } from "@backend/src/schemas/taskSchema";
import useTaskDependenciesMutation from "@/api/mutation/useTaskDependenciesmutaion";
import useRemoveTaskDependenciesMutation from "@/api/mutation/useTaskRemoveDependencies";
import useCreateTaskMutation, { Task } from "@/api/mutation/useTaskCreateMutation";
import useProjectQuery from "@/api/query/useProjectQuery";
import { generateUniqueTaskName } from "@/components/shared/taskNameGenrater";
import { errorConfig } from "@/lib/toastError";
import { errorKeyFormate } from "@/helperFuntions/errroMessage";
import TrashCan from "@/assets/svg/TrashCan.svg";
import RedFlag from "@/assets/svg/RedFlag.svg";
import OrangeFlag from "@/assets/svg/OrangeFlag.svg";
import GreenFlag from "@/assets/svg/GreenFlag.svg";
import { formatSubtaskDate } from "./TaskSubtasksList";

interface TaskDependenciesSectionProps {
  projectId?: string;
  task?: Task;
  refetch: () => void;
  allowed: boolean;
  setTaskId: (id: string) => void;
  setTask: (task: Task) => void;
  allTask: Task[];
  endTask?: string;
}

type OptionType = { value: string; label: string };

const selectCustomStyles = {
  control: (provided: Record<string, unknown>, state: { isFocused: boolean }) => ({
    ...provided,
    minHeight: "38px",
    height: "38px",
    borderRadius: "0.5rem",
    borderColor: state.isFocused ? "#3B82F6" : "#E2E8F0",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.15)" : "none",
    fontSize: "0.8125rem",
    backgroundColor: "#FFFFFF",
    "&:hover": {
      borderColor: state.isFocused ? "#3B82F6" : "#CBD5E1",
    },
  }),
  valueContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    padding: "0 10px",
  }),
  input: (provided: Record<string, unknown>) => ({
    ...provided,
    margin: "0px",
    fontSize: "0.8125rem",
  }),
  placeholder: (provided: Record<string, unknown>) => ({
    ...provided,
    color: "#94A3B8",
    fontSize: "0.8125rem",
  }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    borderRadius: "0.75rem",
    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.05)",
    border: "1px solid #E2E8F0",
    zIndex: 9999,
    overflow: "hidden",
    marginTop: "4px",
  }),
  menuList: (provided: Record<string, unknown>) => ({
    ...provided,
    padding: "4px",
  }),
  option: (
    provided: Record<string, unknown>,
    state: { isSelected: boolean; isFocused: boolean }
  ) => ({
    ...provided,
    borderRadius: "0.375rem",
    fontSize: "0.8125rem",
    padding: "8px 12px",
    backgroundColor: state.isSelected
      ? "#3B82F6"
      : state.isFocused
      ? "#EFF6FF"
      : "transparent",
    color: state.isSelected ? "#FFFFFF" : state.isFocused ? "#1E40AF" : "#1E293B",
    cursor: "pointer",
  }),
};

export const TaskDependenciesSection: React.FC<TaskDependenciesSectionProps> = ({
  projectId,
  task,
  refetch,
  allowed,
  setTaskId,
  setTask,
  allTask,
  endTask,
}) => {
  const { t } = useTranslation();
  const [isAdding, setIsAdding] = useState(Boolean(endTask));
  const [deleteDependencyId, setDeleteDependencyId] = useState<string>("");

  const taskDependenciesMutation = useTaskDependenciesMutation(task?.taskId);
  const removeTaskDependenciesMutation = useRemoveTaskDependenciesMutation();
  const taskCreateMutation = useCreateTaskMutation(projectId, task?.taskId ?? "");
  const projectsQuery = useProjectQuery();

  const defaultOption = React.useMemo(() => {
    const found = allTask.find((t) => t.taskId === endTask);
    return found ? { label: found.taskName, value: found.taskId } : null;
  }, [allTask, endTask]);

  const [selectedTaskOption, setSelectedTaskOption] = useState<OptionType | null>(defaultOption);

  const availableTaskOptions = useCallback((): OptionType[] => {
    if (!allTask) return [];

    const getAncestors = (taskId: string, tasks: Task[]): string[] => {
      const current = tasks.find((t) => t.taskId === taskId);
      if (!current || !current.parentTaskId) return [];
      return [current.parentTaskId, ...getAncestors(current.parentTaskId, tasks)];
    };

    const getDescendants = (taskId: string, tasks: Task[]): string[] => {
      const children = tasks.filter((t) => t.parentTaskId === taskId);
      return children.length > 0
        ? children.flatMap((sub) => [sub.taskId, ...getDescendants(sub.taskId, tasks)])
        : [];
    };

    const ancestors = task?.taskId ? getAncestors(task.taskId, allTask) : [];
    const descendants = task?.taskId ? getDescendants(task.taskId, allTask) : [];

    return allTask
      .filter((t) => {
        if (t.taskId === task?.taskId) return false;
        if (ancestors.includes(t.taskId)) return false;
        if (descendants.includes(t.taskId)) return false;
        return true;
      })
      .map((t) => ({ label: t.taskName, value: t.taskId }));
  }, [allTask, task]);

  const ensureTaskCreated = async () => {
    if (task?.taskId) return task.taskId;
    const project = projectsQuery?.data?.data?.data?.find((p) => p.projectId === projectId);
    const projectStartDate = project?.startDate ? new Date(project.startDate) : new Date();

    const data = {
      taskName: generateUniqueTaskName(allTask ?? [], project?.projectName ?? ""),
      taskDescription: "",
      startDate: projectStartDate >= new Date() ? projectStartDate : new Date(),
      duration: 1.0,
    };

    const res = await taskCreateMutation.mutateAsync(data);
    setTaskId(res.data.data.taskId);
    setTask(res.data.data);
    return res.data.data.taskId;
  };

  const dependenciesFormik = useFormik<z.infer<typeof dependenciesTaskSchema>>({
    initialValues: {
      dependentType: TaskDependenciesEnumValue.PREDECESSORS,
      dependendentOnTaskId: endTask ? defaultOption?.value ?? "" : "",
    },
    validationSchema: toFormikValidationSchema(dependenciesTaskSchema),
    onSubmit: async (values) => {
      try {
        const activeTaskId = await ensureTaskCreated();
        taskDependenciesMutation.mutate(
          { ...values, taskId: activeTaskId },
          {
            onSuccess: (data) => {
              refetch();
              setSelectedTaskOption(null);
              setIsAdding(false);
              toast.success(data.data.message);
              dependenciesFormik.resetForm();
            },
            onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
              toast.error(
                errorKeyFormate(err?.response?.data?.message || err?.message || ""),
                errorConfig
              );
            },
          }
        );
      } catch (err: unknown) {
        toast.error(errorKeyFormate((err as Error).message || ""), errorConfig);
      }
    },
  });

  const handleDeleteDependency = () => {
    if (!deleteDependencyId) return;
    removeTaskDependenciesMutation.mutate(deleteDependencyId, {
      onSuccess: (data) => {
        refetch();
        toast.success(data.data.message);
        setDeleteDependencyId("");
      },
      onError: (err: { response?: { data?: { message?: string } }; message?: string }) => {
        toast.error(
          errorKeyFormate(err?.response?.data?.message || err?.message || ""),
          errorConfig
        );
        setDeleteDependencyId("");
      },
    });
  };

  const dependenciesList = React.useMemo(() => {
    return (task?.dependencies || []).filter(
      (dep) => !dep.dependentOnTask?.deletedAt && !dep.dependentOnTask?.taskName?.includes("_deleted_")
    );
  }, [task?.dependencies]);

  // Group dependencies into Predecessors and Successors
  const { predecessors, successors } = React.useMemo(() => {
    const predList: typeof dependenciesList = [];
    const succList: typeof dependenciesList = [];

    dependenciesList.forEach((dep) => {
      if (dep.dependentType === TaskDependenciesEnumValue.PREDECESSORS) {
        predList.push(dep);
      } else if (dep.dependentType === TaskDependenciesEnumValue.SUCCESSORS) {
        succList.push(dep);
      }
    });

    return { predecessors: predList, successors: succList };
  }, [dependenciesList]);

  const renderFlagIcon = (flag?: string) => {
    const normalized = flag?.trim().toLowerCase();
    if (normalized === "orange") {
      return <img src={OrangeFlag} alt={t("MODERATE_DELAY")} className="w-4 h-4 shrink-0 object-contain" />;
    }
    if (normalized === "red") {
      return <img src={RedFlag} alt={t("SIGNIFICANT_DELAY")} className="w-4 h-4 shrink-0 object-contain" />;
    }
    // Default to GreenFlag for "green" or when flag is not set/on time
    return <img src={GreenFlag} alt={t("ON_TIME")} className="w-4 h-4 shrink-0 object-contain" />;
  };

  const renderDependencyGroup = (
    title: string,
    items: typeof dependenciesList
  ) => {
    return (
      <div className="flex flex-col gap-2 p-3.5 bg-slate-50/50 border border-slate-200/80 rounded-2xl">
        {/* Group Header */}
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-md bg-blue-100 text-blue-600">
            <ArrowRight className="w-3.5 h-3.5" />
          </div>
          <span className="text-xs font-bold text-slate-800">
            {title} ({items.length})
          </span>
        </div>

        {/* Group Table */}
        <div className="flex flex-col overflow-x-auto mt-1">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-3 px-3 py-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider min-w-[550px]">
            <div className="col-span-6">{t("task")}</div>
            <div className="col-span-3">{t("start")}</div>
            <div className="col-span-2">{t("end")}</div>
            <div className="col-span-1 text-right"></div>
          </div>

          {/* Table Rows */}
          <div className="flex flex-col gap-1.5 min-w-[550px]">
            {items.map((dep) => {
              const targetTaskId = dep.dependentOnTask?.taskId || dep.dependendentOnTaskId;
              const matchedFromAll = allTask.find((t) => t.taskId === targetTaskId);
              const depTask = {
                ...dep.dependentOnTask,
                ...matchedFromAll,
                taskName: matchedFromAll?.taskName || dep.dependentOnTask?.taskName,
                startDate: matchedFromAll?.startDate || dep.dependentOnTask?.startDate,
                endDate: matchedFromAll?.endDate || dep.dependentOnTask?.endDate,
                flag: matchedFromAll?.flag || dep.dependentOnTask?.flag,
              };

              return (
                <div
                  key={dep.taskDependenciesId}
                  onClick={() => {
                    if (targetTaskId) {
                      setTaskId(targetTaskId);
                    }
                  }}
                  className="group grid grid-cols-12 gap-3 items-center px-3 py-2.5 bg-white hover:bg-slate-50 border border-slate-200/70 hover:border-slate-300 rounded-xl text-xs transition-all cursor-pointer shadow-2xs select-none"
                >
                  {/* Task Name & Flag */}
                  <div className="col-span-6 flex items-center gap-2.5 min-w-0 pr-2">
                    <div className="shrink-0">{renderFlagIcon(depTask?.flag)}</div>
                    <span
                      className="font-medium text-slate-800 group-hover:text-primary-600 truncate transition-colors"
                      title={depTask?.taskName}
                    >
                      {depTask?.taskName || t("unknown")}
                    </span>
                  </div>

                  {/* Start Date */}
                  <div className="col-span-3 text-slate-600 font-medium truncate">
                    {formatSubtaskDate(depTask?.startDate)}
                  </div>

                  {/* End Date */}
                  <div className="col-span-2 text-slate-600 font-medium truncate">
                    {formatSubtaskDate(depTask?.endDate)}
                  </div>

                  {/* Chevron Right & Delete Bin Icon */}
                  <div className="col-span-1 flex items-center justify-end gap-1.5">
                    {allowed && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteDependencyId(dep.taskDependenciesId);
                        }}
                        className="p-1 rounded-md text-red-500 hover:text-red-700 hover:bg-red-50 border border-transparent hover:border-red-100 transition-all cursor-pointer"
                        title={t("delete")}
                      >
                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                      </button>
                    )}
                    <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-3.5 bg-white border border-slate-200/90 rounded-2xl p-4 shadow-2xs">
      {/* Header bar */}
      <div className="flex items-center justify-between pb-2 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-md bg-blue-50 text-blue-600">
            <Link2 className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                {t("dependencies")}
              </h4>
              <span className="text-xs font-semibold text-slate-400">
                ({dependenciesList.length})
              </span>
            </div>
            <p className="text-[11px] text-slate-400">
              {t("link_tasks_define_sequence")}
            </p>
          </div>
        </div>

        {!isAdding && allowed && (
          <Button
            type="button"
            variant="none"
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-1.5 h-8 px-3 rounded-lg text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors shadow-2xs cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5 text-white" />
            <span>{t("add_dependency")}</span>
          </Button>
        )}
      </div>

      {/* Existing Dependencies Lists - Predecessors first, then Successors */}
      {dependenciesList.length > 0 && (
        <div className="flex flex-col gap-3">
          {predecessors.length > 0 &&
            renderDependencyGroup(t("PREDECESSORS"), predecessors)}
          {successors.length > 0 &&
            renderDependencyGroup(t("SUCCESSORS"), successors)}
        </div>
      )}

      {/* Add New Dependency Form Card */}
      {isAdding && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            dependenciesFormik.handleSubmit();
          }}
          className="flex flex-col gap-3 p-3.5 rounded-xl border border-blue-200 bg-blue-50/30"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-slate-800">
              {t("new_dependency")}
            </span>
            <button
              type="button"
              onClick={() => {
                dependenciesFormik.resetForm();
                setIsAdding(false);
              }}
              className="text-xs text-slate-400 hover:text-slate-600 font-medium"
            >
              {t("cancel")}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5 items-center">
            {/* Relationship Type Dropdown */}
            <div className="sm:col-span-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="w-full flex items-center justify-between h-[38px] px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-xs font-semibold text-slate-700 shadow-2xs transition-colors"
                  >
                    <span>{t(dependenciesFormik.values.dependentType)}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-48 bg-white shadow-xl rounded-xl border border-slate-200 p-1 z-[9999]">
                  {Object.keys(TaskDependenciesEnumValue).map((key) => {
                    const enumVal = TaskDependenciesEnumValue[key as keyof typeof TaskDependenciesEnumValue];
                    const isSelected = dependenciesFormik.values.dependentType === enumVal;
                    return (
                      <DropdownMenuItem
                        key={key}
                        onClick={() => dependenciesFormik.setFieldValue("dependentType", enumVal)}
                        className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                          isSelected ? "bg-blue-50 text-blue-700" : "text-slate-700 hover:bg-slate-100"
                        }`}
                      >
                        <span>{t(enumVal)}</span>
                        {isSelected && <Check className="w-3.5 h-3.5 text-blue-600" />}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Target Task Select */}
            <div className="sm:col-span-8">
              <Select
                placeholder={t("select_related_task")}
                styles={selectCustomStyles}
                value={selectedTaskOption}
                options={availableTaskOptions()}
                onChange={(option: SingleValue<OptionType>) => {
                  setSelectedTaskOption(option);
                  dependenciesFormik.setFieldValue("dependendentOnTaskId", option?.value || "");
                }}
                onBlur={() => dependenciesFormik.setFieldTouched("dependendentOnTaskId")}
                isDisabled={Boolean(endTask)}
                menuPlacement="auto"
                isClearable
              />
            </div>
          </div>

          {/* Form validation errors */}
          {dependenciesFormik.touched.dependendentOnTaskId && dependenciesFormik.errors.dependendentOnTaskId && (
            <span className="text-[11px] text-rose-500 font-medium">
              {t(dependenciesFormik.errors.dependendentOnTaskId)}
            </span>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="none"
              onClick={() => {
                dependenciesFormik.resetForm();
                setIsAdding(false);
              }}
              className="text-xs text-slate-500 hover:text-slate-700 px-3 py-1.5 h-auto"
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              variant="none"
              disabled={
                !dependenciesFormik.values.dependendentOnTaskId ||
                taskDependenciesMutation.isPending ||
                taskCreateMutation.isPending
              }
              className={`text-xs px-3.5 py-1.5 rounded-lg font-semibold transition-all h-auto shadow-2xs ${
                dependenciesFormik.values.dependendentOnTaskId
                  ? "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                  : "bg-slate-100 text-slate-400 cursor-not-allowed opacity-60"
              }`}
            >
              {taskDependenciesMutation.isPending ? t("Saving...") : t("add_link_action")}
            </Button>
          </div>
        </form>
      )}

      {/* Empty State when no dependencies exist and not adding */}
      {dependenciesList.length === 0 && !isAdding && (
        <div className="text-center py-6 border border-dashed border-slate-200 rounded-xl">
          <Link2 className="w-8 h-8 text-slate-300 mx-auto mb-1.5" />
          <p className="text-xs font-medium text-slate-600">
            {t("no_dependencies_configured")}
          </p>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {t("set_predecessor_or_successor_tasks_to_manage_your_project_workflow")}
          </p>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Dialog
        isOpen={Boolean(deleteDependencyId)}
        onClose={() => setDeleteDependencyId("")}
        modalClass="rounded-xl max-w-sm p-5"
      >
        <div className="flex flex-col items-center text-center gap-3">
          <img src={TrashCan} className="w-12 h-12" alt="Trash" />
          <h3 className="font-bold text-slate-800 text-sm">
            {t("delete_dependency_confirm_msg")}
          </h3>
          <div className="flex items-center gap-2 mt-2 w-full">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeleteDependencyId("")}
              className="flex-1 text-xs"
            >
              {t("cancel")}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteDependency}
              isLoading={removeTaskDependenciesMutation.isPending}
              className="flex-1 text-xs"
            >
              {t("delete")}
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default TaskDependenciesSection;
